import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiCheck, FiPackage, FiTruck, FiCheckCircle, FiXCircle } = FiIcons;

const OrderTimeline = ({ status }) => {
  const steps = [
    { id: 'pending', label: 'Placed', icon: FiClock },
    { id: 'accepted', label: 'Accepted', icon: FiCheck },
    { id: 'preparing', label: 'Kitchen', icon: FiPackage },
    { id: 'ready', label: 'Ready', icon: FiTruck },
    { id: 'completed', label: 'Finished', icon: FiCheckCircle },
  ];

  const terminalStates = ['declined', 'cancelled'];
  const isTerminal = terminalStates.includes(status);

  if (isTerminal) {
    return (
      <div className="flex items-center p-6 bg-red-50 rounded-3xl border border-red-100 mt-6">
        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mr-4 shadow-sm">
          <SafeIcon icon={FiXCircle} className="text-red-600 text-xl" />
        </div>
        <div>
          <p className="font-black text-red-900 uppercase tracking-widest text-[10px]">Order {status}</p>
          <p className="text-[10px] text-red-600 font-bold">This transaction has been terminated.</p>
        </div>
      </div>
    );
  }

  const getStepIndex = (s) => steps.findIndex(step => step.id === s);
  const currentIndex = getStepIndex(status);

  return (
    <div className="mt-8 px-2 md:px-4">
      {/* Desktop View (Horizontal) */}
      <div className="hidden md:block relative">
        <div className="relative flex justify-between">
          {/* Background Line */}
          <div className="absolute top-6 left-0 w-full h-1 bg-slate-100 -z-0 rounded-full" />
          {/* Progress Line */}
          <div 
            className="absolute top-6 left-0 h-1 bg-indigo-600 transition-all duration-1000 -z-0 rounded-full" 
            style={{ width: `${(currentIndex / (steps.length - 1)) * 100}%` }} 
          />

          {steps.map((step, index) => {
            const isCompleted = index <= currentIndex;
            const isCurrent = index === currentIndex;
            return (
              <div key={step.id} className="flex flex-col items-center relative z-10 w-1/5">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border-2 transition-all duration-500 ${isCompleted ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100' : 'bg-white border-slate-200 text-slate-300'} ${isCurrent ? 'ring-8 ring-indigo-50 scale-110' : ''}`} >
                  <SafeIcon icon={step.icon} className="text-lg" />
                </div>
                <span className={`mt-4 text-[9px] font-black uppercase tracking-[0.2em] text-center ${isCompleted ? 'text-indigo-600' : 'text-slate-400'}`}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile View (Vertical) */}
      <div className="md:hidden space-y-6 relative ml-4">
        {/* Vertical Progress Line */}
        <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-100 -z-0" />
        <div 
          className="absolute left-5 top-0 w-0.5 bg-indigo-600 transition-all duration-1000 -z-0" 
          style={{ height: `${(currentIndex / (steps.length - 1)) * 100}%` }} 
        />

        {steps.map((step, index) => {
          const isCompleted = index <= currentIndex;
          const isCurrent = index === currentIndex;
          return (
            <div key={step.id} className="flex items-center relative z-10">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center border-2 transition-all duration-500 flex-shrink-0 ${isCompleted ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100' : 'bg-white border-slate-200 text-slate-300'} ${isCurrent ? 'ring-4 ring-indigo-50' : ''}`} >
                <SafeIcon icon={step.icon} className="text-sm" />
              </div>
              <div className="ml-6">
                <p className={`text-[10px] font-black uppercase tracking-widest ${isCompleted ? 'text-indigo-600' : 'text-slate-400'}`}>
                  {step.label}
                </p>
                {isCurrent && (
                  <p className="text-[9px] font-bold text-slate-400 uppercase mt-0.5">Current Status</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderTimeline;